<script >
import { ref, watch } from '@vue/composition-api';
import { ageOptions, directionOptions, dividendOptions, entryDistanceOptions, entryTypeOptions, profitabilityOptions, timingOptions, valueTypeOptions } from '@/features/charting-next/models/chartingFinderOptionCatalog';
const __sfc_main = {};
__sfc_main.props = {
  canUseFilters: {
    type: Boolean,
    default: false
  },
  mutationError: {
    type: String,
    default: ''
  },
  isReady: {
    type: Boolean,
    default: false
  },
  filters: {
    type: Object,
    default: null
  },
  options: {
    type: Object,
    default: () => ({
      currencies: [],
      countries: [],
      sectors: [],
      industries: []
    })
  },
  filterAccess: {
    type: Object,
    default: () => ({})
  },
  isApplying: {
    type: Boolean,
    default: false
  },
  isResetting: {
    type: Boolean,
    default: false
  },
  hasPendingChanges: {
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const visibleTimingOptions = timingOptions.filter(option => option.key !== 'ipo');
  const props = __props;
  const emit = __ctx.emit;
  const cloneFilters = value => JSON.parse(JSON.stringify(value ?? null));
  const serializeFilters = value => JSON.stringify(value ?? null);
  const localFilters = ref(cloneFilters(null));
  let syncingFromProps = false;
  watch(() => props.filters, value => {
    const nextValue = cloneFilters(value);
    if (serializeFilters(nextValue) === serializeFilters(localFilters.value)) {
      return;
    }
    syncingFromProps = true;
    localFilters.value = nextValue;
    syncingFromProps = false;
  }, {
    deep: true,
    immediate: true
  });
  watch(localFilters, value => {
    if (syncingFromProps) {
      return;
    }
    const nextValue = cloneFilters(value);
    if (serializeFilters(nextValue) === serializeFilters(props.filters)) {
      return;
    }
    emit('update:filters', nextValue);
  }, {
    deep: true
  });
  const toggleCollectionValue = (collection, value) => {
    if (!Array.isArray(collection)) {
      return;
    }
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return;
    }
    const currentIndex = collection.indexOf(normalizedValue);
    if (currentIndex >= 0) {
      collection.splice(currentIndex, 1);
      return;
    }
    collection.push(normalizedValue);
  };
  const isCollectionValueSelected = (collection, value) => Array.isArray(collection) && collection.includes(String(value || '').trim());
  return {
    ageOptions,
    directionOptions,
    dividendOptions,
    entryDistanceOptions,
    entryTypeOptions,
    profitabilityOptions,
    valueTypeOptions,
    visibleTimingOptions,
    localFilters,
    toggleCollectionValue,
    isCollectionValueSelected
  };
};
export default __sfc_main;
</script>

<template>
    <section class="charting-workspace-drawer-section">
        <div class="flex items-center justify-between gap-3">
            <div>
                <div class="text-xs font-semibold uppercase tracking-wide text-muted-color">Filtros avanzados</div>
                <div class="mt-1 text-sm text-muted-color">Añade criterios al conjunto seleccionado.</div>
            </div>
            <Tag v-if="!canUseFilters" value="Restringido" severity="warn" />
        </div>

        <div v-if="mutationError" class="brand-banner-warning mt-4 rounded-xl border p-4 text-sm">
            {{ mutationError }}
        </div>

        <div v-if="!canUseFilters" class="brand-empty-state mt-4 rounded-xl border border-dashed p-4 text-sm">Tu acceso actual no permite editar el finder.</div>

        <div v-else-if="isReady && localFilters" class="mt-4 flex flex-col gap-4">
            <details open class="charting-workspace-filter-section">
                <summary>Requeridos</summary>
                <div class="mt-4 flex flex-col gap-4">
                    <div>
                        <div class="charting-workspace-subsection-title">Rentabilidad</div>
                        <div class="charting-workspace-choice-grid">
                            <label v-for="option in profitabilityOptions" :key="option.key" class="charting-workspace-choice">
                                <input v-model="localFilters.profitabilityBands[option.key]" type="checkbox" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div class="charting-workspace-subsection-title">Timing</div>
                        <div class="charting-workspace-choice-grid compact">
                            <label v-for="option in visibleTimingOptions" :key="option.key" class="charting-workspace-choice">
                                <input v-model="localFilters.timing[option.key]" type="checkbox" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div class="charting-workspace-subsection-title">Dirección</div>
                        <div class="charting-workspace-choice-grid compact">
                            <label v-for="option in directionOptions" :key="option.key" class="charting-workspace-choice">
                                <input v-model="localFilters.directions[option.key]" type="checkbox" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div class="charting-workspace-subsection-title">Tipo de entrada</div>
                        <div class="charting-workspace-choice-grid">
                            <label v-for="option in entryTypeOptions" :key="option.key" class="charting-workspace-choice">
                                <input v-model="localFilters.entryTypes[option.key]" type="checkbox" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div class="charting-workspace-subsection-title">Tipo de valor</div>
                        <div class="charting-workspace-choice-grid compact">
                            <label v-for="option in valueTypeOptions" :key="option.key" class="charting-workspace-choice">
                                <input v-model="localFilters.valueTypes[option.key]" type="checkbox" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </details>

            <details open class="charting-workspace-filter-section">
                <summary>Advisables</summary>
                <div class="mt-4 flex flex-col gap-4">
                    <div class="grid gap-3 sm:grid-cols-2">
                        <div>
                            <div class="flex items-center justify-between gap-3">
                                <label class="charting-workspace-field-label" for="charting-workspace-amount-min">Montante mínimo</label>
                                <Tag v-if="!filterAccess.amount" value="Bloqueado" severity="warn" />
                            </div>
                            <InputText id="charting-workspace-amount-min" v-model="localFilters.amount.min" class="mt-2 w-full" inputmode="decimal" placeholder="0" :disabled="!filterAccess.amount" />
                        </div>
                        <div>
                            <label class="charting-workspace-field-label" for="charting-workspace-amount-max">Montante maximo</label>
                            <InputText id="charting-workspace-amount-max" v-model="localFilters.amount.max" class="mt-2 w-full" inputmode="decimal" placeholder="Sin limite" :disabled="!filterAccess.amount" />
                        </div>
                    </div>

                    <div class="grid gap-3 sm:grid-cols-2">
                        <div>
                            <div class="flex items-center justify-between gap-3">
                                <label class="charting-workspace-field-label" for="charting-workspace-entry-min">Distancia min.</label>
                                <Tag v-if="!filterAccess.entryDistance" value="Bloqueado" severity="warn" />
                            </div>
                            <select id="charting-workspace-entry-min" v-model="localFilters.entryDistance.min" class="charting-workspace-native-select mt-2" :disabled="!filterAccess.entryDistance">
                                <option v-for="option in entryDistanceOptions" :key="`entry-min-${option.value}`" :value="option.value">{{ option.label }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="charting-workspace-field-label" for="charting-workspace-entry-max">Distancia max.</label>
                            <select id="charting-workspace-entry-max" v-model="localFilters.entryDistance.max" class="charting-workspace-native-select mt-2" :disabled="!filterAccess.entryDistance">
                                <option v-for="option in entryDistanceOptions" :key="`entry-max-${option.value}`" :value="option.value">{{ option.label }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="grid gap-3 sm:grid-cols-2">
                        <div>
                            <div class="flex items-center justify-between gap-3">
                                <label class="charting-workspace-field-label" for="charting-workspace-age-min">Edad minima</label>
                                <Tag v-if="!filterAccess.age" value="Bloqueado" severity="warn" />
                            </div>
                            <select id="charting-workspace-age-min" v-model="localFilters.age.min" class="charting-workspace-native-select mt-2" :disabled="!filterAccess.age">
                                <option v-for="option in ageOptions" :key="`age-min-${option.value}`" :value="option.value">{{ option.label }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="charting-workspace-field-label" for="charting-workspace-age-max">Edad maxima</label>
                            <select id="charting-workspace-age-max" v-model="localFilters.age.max" class="charting-workspace-native-select mt-2" :disabled="!filterAccess.age">
                                <option v-for="option in ageOptions" :key="`age-max-${option.value}`" :value="option.value">{{ option.label }}</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <div class="flex items-center justify-between gap-3">
                            <div class="charting-workspace-subsection-title">Dividendos</div>
                            <Tag v-if="!filterAccess.dividends" value="Bloqueado" severity="warn" />
                        </div>
                        <div class="charting-workspace-choice-grid compact">
                            <label v-for="option in dividendOptions" :key="option.key" class="charting-workspace-choice">
                                <input v-model="localFilters.dividends[option.key]" type="checkbox" :disabled="!filterAccess.dividends" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </details>

            <details open class="charting-workspace-filter-section">
                <summary>Universo</summary>
                <div class="mt-4 flex flex-col gap-4">
                    <div>
                        <div class="flex items-center justify-between gap-3">
                            <div class="charting-workspace-subsection-title">Divisas</div>
                            <Tag v-if="!filterAccess.currency" value="Bloqueado" severity="warn" />
                        </div>
                        <div class="charting-workspace-selection-list mt-3">
                            <label v-for="option in options.currencies" :key="`currency-${option.value}`" class="charting-workspace-check-row">
                                <input type="checkbox" :checked="isCollectionValueSelected(localFilters.currencies, option.value)" :disabled="!filterAccess.currency" @change="toggleCollectionValue(localFilters.currencies, option.value)" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div class="flex items-center justify-between gap-3">
                            <div class="charting-workspace-subsection-title">Paises</div>
                            <Tag v-if="!filterAccess.country" value="Bloqueado" severity="warn" />
                        </div>
                        <div class="charting-workspace-selection-list mt-3">
                            <label v-for="option in options.countries" :key="`country-${option.value}`" class="charting-workspace-check-row">
                                <input type="checkbox" :checked="isCollectionValueSelected(localFilters.countries, option.value)" :disabled="!filterAccess.country" @change="toggleCollectionValue(localFilters.countries, option.value)" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div class="flex items-center justify-between gap-3">
                            <div class="charting-workspace-subsection-title">Sectores</div>
                            <Tag v-if="!filterAccess.sector" value="Bloqueado" severity="warn" />
                        </div>
                        <div class="charting-workspace-selection-list mt-3">
                            <label v-for="option in options.sectors" :key="`sector-${option.value}`" class="charting-workspace-check-row">
                                <input type="checkbox" :checked="isCollectionValueSelected(localFilters.sectors, option.value)" :disabled="!filterAccess.sector" @change="toggleCollectionValue(localFilters.sectors, option.value)" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div class="flex items-center justify-between gap-3">
                            <div class="charting-workspace-subsection-title">Industrias</div>
                            <Tag v-if="!filterAccess.industry" value="Bloqueado" severity="warn" />
                        </div>
                        <div class="charting-workspace-selection-list mt-3">
                            <label v-for="option in options.industries" :key="`industry-${option.value}`" class="charting-workspace-check-row">
                                <input type="checkbox" :checked="isCollectionValueSelected(localFilters.industries, option.value)" :disabled="!filterAccess.industry" @change="toggleCollectionValue(localFilters.industries, option.value)" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </details>

        </div>

        <div v-else class="mt-4 flex items-center justify-center">
            <ProgressSpinner style="width: 32px; height: 32px" stroke-width="6" />
        </div>
    </section>
</template>
